// Tryb — the conduct code, and the panel that opens it.
//
// "I've read the conduct code" has to be readable, so the onboarding step
// carries the code itself rather than a claim about one. Accepting records the
// version the facilitator actually saw.

import { useState } from "react";
import { Badge, Button, Dialog, Icon } from "../ds/index.js";
import { CONDUCT_CODE } from "../content/conductCode.js";

export function ConductCodeDialog({ onClose, onAccept, accepted }) {
  return (
    <Dialog width={680} title="The tryb host code" onClose={onClose}>
      <div style={{ fontFamily: "var(--font-body)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: -2, marginBottom: 16 }}>
          <Badge tone="neutral">Version {CONDUCT_CODE.version}</Badge>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)" }}>
            Updated {CONDUCT_CODE.updated}
          </span>
        </div>

        <p style={{ margin: "0 0 22px", fontSize: "var(--text-base)", lineHeight: 1.6, color: "var(--text-secondary)" }}>
          {CONDUCT_CODE.intro}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {CONDUCT_CODE.sections.map((s) => (
            <section key={s.id}>
              <h3
                style={{
                  margin: "0 0 10px",
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-md)",
                  fontWeight: 800,
                  letterSpacing: "var(--tracking-snug)",
                }}
              >
                {s.title}
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                {s.points.map((p, i) => (
                  <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ color: "var(--sage-600)", flex: "none", marginTop: 2, display: "inline-flex" }}>
                      <Icon name="Check" size={16} sw={2.4} />
                    </span>
                    <span style={{ fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--text-secondary)" }}>{p}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div
          style={{
            marginTop: 24,
            padding: "16px 18px",
            borderRadius: "var(--radius-lg)",
            background: "var(--surface-sunken)",
            fontSize: "var(--text-sm)",
            lineHeight: 1.6,
            color: "var(--text-secondary)",
          }}
        >
          {CONDUCT_CODE.closing}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!accepted && (
            <Button variant="primary" onClick={onAccept}>
              I’ve read it — I’m in
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Sits above the conduct step's fields: the short version, plus the way in to
 * the full text. Accepting from the dialog sets the same answer the toggle does.
 */
export default function ConductCodePanel({ accepted, onAccept }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        style={{
          gridColumn: "span 2",
          background: "var(--surface-raised)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-lg)",
          padding: 22,
          display: "flex",
          alignItems: "center",
          gap: 18,
        }}
      >
        <div
          style={{
            width: 56, height: 56, borderRadius: "var(--radius-md)", flex: "none",
            background: accepted ? "var(--sage-100)" : "var(--sand-200)",
            color: accepted ? "var(--sage-700)" : "var(--ink-700)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name={accepted ? "Check" : "Book"} size={26} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "var(--text-md)" }}>The tryb host code</span>
            <Badge tone={accepted ? "success" : "neutral"}>
              {accepted ? "Accepted" : `Version ${CONDUCT_CODE.version}`}
            </Badge>
          </div>
          <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.5, marginTop: 4 }}>
            {CONDUCT_CODE.summary} Six short sections — about two minutes.
          </div>
        </div>

        <Button variant={accepted ? "secondary" : "primary"} onClick={() => setOpen(true)}>
          {accepted ? "Read it again" : "Read the code"}
        </Button>
      </div>

      {open && (
        <ConductCodeDialog
          accepted={accepted}
          onClose={() => setOpen(false)}
          onAccept={() => {
            onAccept();
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
